import { useCallback, useMemo, useState } from 'react';
import {
    type MultiPageVideoModel,
    type MultiPageImageModel,
    TextElement,
    getElementType,
    ContentProperty,
    ElementForContentProperty,
} from '@bynder-studio/render-core';
import useEditor from '~/hooks/useEditor';

export function useContentProperty(
    creativeModel: MultiPageVideoModel | MultiPageImageModel,
    selectedElements: ElementForContentProperty[],
) {
    const { manipulationRenderer } = useEditor();
    const contentPropertiesManager = creativeModel.getContentPropertiesManager();
    const [searchValue, setSearchValue] = useState('');
    const [creationTime, setCreationTime] = useState(0);
    const searchTerm = searchValue.trim().toLowerCase();

    const unlockedElements = selectedElements.filter((el) => !el.locked);
    const primaryElement = selectedElements[0];
    const isMultiSelect = selectedElements.length > 1;

    const sharedContentPropertyId = useMemo(() => {
        if (!selectedElements.length) {
            return '';
        }

        const ids = selectedElements.map((el) => el?.contentPropertyId).filter(Boolean);

        return ids.length && ids.every((id) => id === ids[0]) ? ids[0] : '';
    }, [selectedElements.map((el) => `${el?.contentPropertyId ?? ''}:${el?.locked ? 1 : 0}`).join('|')]);

    const currentProperty = sharedContentPropertyId.length
        ? contentPropertiesManager.getContentProperty(sharedContentPropertyId)
        : null;
    const elementType = primaryElement ? getElementType(primaryElement) : null;

    const handleSearchChange = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const applyProperty = (
        element: ElementForContentProperty,
        contentPropertyId: string,
        applyPropertySettings = false,
    ) => {
        if (!selectedElements.length) {
            return;
        }

        let properties = {};

        if (applyPropertySettings) {
            properties = contentPropertiesManager.getContentProperty(contentPropertyId)?.properties ?? {};
        }

        if (element instanceof TextElement && element.isTextEditorActivated) {
            manipulationRenderer.canvasManipulation.disableTextEditor();
        }

        creativeModel.updateElement(element.id, {
            contentPropertyId,
            ...properties,
        });
    };

    const onCreate = (name?: string) => {
        if (!unlockedElements.length) {
            return;
        }

        if (!isMultiSelect && !name?.length) {
            return;
        }

        // In order to prevent double click
        if (Date.now() - creationTime < 500) {
            return;
        }

        setCreationTime(Date.now());

        unlockedElements.forEach((element) => {
            if (element.contentPropertyId) {
                return;
            }

            const contentPropertyName = isMultiSelect ? element.name : name;

            const newPropertyId = contentPropertiesManager.createContentPropertyFromElement(
                element,
                contentPropertyName,
            );

            if (newPropertyId) {
                applyProperty(element, newPropertyId);
            }
        });
    };

    const onDetach = () => {
        if (!unlockedElements.length) {
            return;
        }

        unlockedElements.forEach((element) => {
            creativeModel.updateElement(element.id, { contentPropertyId: '' });
        });
    };

    const onRename = (name: string) => {
        const updatedProperty = { ...currentProperty, name };
        contentPropertiesManager.updateContentProperty(updatedProperty);
    };

    const onDelete = () => {
        if (currentProperty) {
            contentPropertiesManager.deleteContentProperty(currentProperty.uuid);
        }
    };

    const onSave = (updatedProperties: ContentProperty[]) => {
        contentPropertiesManager.updateContentProperties(updatedProperties);
    };

    const allTypesProperties = contentPropertiesManager.allContentProperties;
    // @ts-ignore
    const propertiesByType = contentPropertiesManager.getContentPropertiesByType(elementType);

    const filteredProperties =
        !searchTerm || !searchTerm.length
            ? propertiesByType
            : propertiesByType.filter((property) => property.name.toLowerCase().includes(searchTerm));

    return {
        allTypesProperties,
        filteredProperties,
        currentProperty,
        searchValue,
        handleSearchChange,
        applyProperty,
        onCreate,
        onDetach,
        onRename,
        onDelete,
        onSave,
    };
}
